import { useEffect, useState } from 'react'
import { Button } from '../../platform/primitives/Button'
import { Field } from '../../platform/primitives/Field'
import { useToast } from '../../platform/feedback/ToastProvider'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { routeValidationErrors } from '../../api/validationError'
import { MerchantLogoField } from './MerchantLogoField'
import { LogoPicker } from './LogoPicker'
import { IconPaneDialog, IconPaneOpener, useIconPane } from '../../platform/icons/IconPane'
import { TYPE_LABEL, type MerchantType } from './merchantFilters'
import {
  MerchantsError,
  useCreateMerchant,
  useUpdateMerchant,
  useUploadLogo,
  type Merchant,
  type MerchantUpdate,
} from './useMerchants'
import './merchants.css'

const TYPES = Object.keys(TYPE_LABEL) as MerchantType[]

/** `MerchantsError`'s own status, narrowed from `unknown` in one place —
 *  same shape `CategoryForm.tsx`'s `statusOf` takes for the identical job. */
function statusOf(cause: unknown): number | undefined {
  return cause instanceof MerchantsError ? cause.status : undefined
}

/**
 * The three server failures worth putting on the field rather than in a
 * toast — final review correctness C2, which this replaces wholesale.
 *
 * What `merchants.py` actually sends, read from the handler rather than
 * assumed: a duplicate name is **400** on BOTH create (`:67-69`, `:84-86`)
 * and edit (`:296-298`) — never 422, which is FastAPI's own genuine
 * validation failure (a name over 255 characters, a bad enum) and used to
 * carry no field-worthy message of its own, because nothing here read the
 * body. Issue #473 gives 422 its own real handling (see `submit`, below) —
 * this function is now the FALLBACK for a 422 whose body carries no usable
 * `detail`, not the only thing a 422 can produce. A write aimed at a GLOBAL
 * merchant is **403** (`:386-389`), raised BEFORE the id lookup that would
 * otherwise 404 — the opposite of `useUpdateMerchant`'s old docblock, which
 * reasoned a global write "falls out at not-found." An id that is missing or
 * not the caller's own is **404** (`:390-393`), and on this path — after the
 * 403 — that really does mean "not found," not "not yours."
 */
function messageFor(status: number | undefined, editing: boolean): string {
  if (status === 400) return 'A merchant with this name already exists.'
  if (status === 403 && editing) return 'This merchant cannot be edited.'
  if (status === 404 && editing) return 'This merchant could not be found.'
  return editing ? 'The merchant could not be saved.' : 'The merchant could not be created.'
}

/** The one field on this form with its own error slot (`Field`'s `error`
 *  prop) — everything else a 422's `loc` might name (`icon`, `type`,
 *  `notes`) has no error-display control of its own, so it is surfaced at
 *  form level instead, through the existing toast idiom. */
const KNOWN_FIELDS = ['name'] as const

/**
 * The single source for every group heading's visible text — read by the
 * JSX below AND by `describeOtherEntry`'s toast copy, so renaming a heading
 * cannot leave the toast quoting the old word (architecture review round 2,
 * issue #473 non-blocking 4: two literal copies of "Type"/"Notes" would be a
 * second source of truth for copy, the same family of defect as the colour
 * rule this repo already enforces once).
 *
 * `name`'s own 422 entries go on the field itself, labelled by `Field`
 * already, so it carries no entry here. A wire identifier (`icon`) is never
 * shown bare in the toast either way: a raw `loc` segment in front of a user
 * is the same class of defect this ticket fixes, one layer up. A field with
 * no label here falls back to the bare message, which is no worse than a
 * label that names nothing the user recognises.
 */
const FIELD_LABELS = {
  icon: 'Icon',
  type: 'Type',
  notes: 'Notes',
} as const

/** One 422 entry the form has no error slot for, as one line of toast copy —
 *  labelled when `FIELD_LABELS` has an entry for its field, bare otherwise. */
function describeOtherEntry(entry: { field: string | undefined; message: string }): string {
  const label =
    entry.field !== undefined && entry.field in FIELD_LABELS
      ? FIELD_LABELS[entry.field as keyof typeof FIELD_LABELS]
      : undefined
  return label !== undefined ? `${label}: ${entry.message}` : entry.message
}

/**
 * A modal, on `Overlay` — same reasoning `CategoryForm.tsx` gives for using
 * it rather than `Dialog.tsx`'s two fixed shapes: a form is neither an
 * announcement nor a fixed one/two-button confirmation. `surface="glass"` is
 * safe here for the same reason it is safe there — this panel carries no
 * `--neg-soft` content, only fields and buttons.
 *
 * **The fields are exactly `MerchantCreate`'s writable set minus two.**
 * `website` is on neither write schema (filed as #294) and is never offered.
 * `icon` IS offered, through the backend icon catalogue — see the Icon group
 * below. `color` is kept ALIVE, not offered: `MerchantAvatar` reads it today
 * for the swatch fallback even though nothing in this form lets a user
 * CHOOSE one yet.
 *
 * **Two shapes, one component — Task 8.**
 *   - no `editing`: create (Task 7).
 *   - `editing`, not `is_global`: an ordinary edit. Save sends only the
 *     fields that changed, and Archive is a second, independent PATCH.
 *   - `editing.is_global`: every field renders disabled and there is no
 *     write control at all — `update_merchant`'s own summary says a global
 *     (Plaid) merchant "cannot be edited," and the handler answers a write
 *     aimed at it with 404 because `user_id` never matches shared reference
 *     data. There is deliberately no "copy this" action either: users must
 *     not fork system merchants into personal ones (product decision
 *     2026-08-21) — they create their own and edit/delete those, never a
 *     copy of shared reference data. This branch has no entry point on any
 *     screen today (the Manage column and merchant detail both skip Edit
 *     for a global row); it stays here as the honest rendering of what
 *     `editing.is_global` means if a future caller ever does reach it.
 */
export function MerchantForm({
  onClose,
  editing: editingProp,
}: {
  onClose: () => void
  editing?: Merchant
}) {
  /* The merchant this form created, when its LOGO then failed to upload —
     #468, and the only way this is ever set.

     A clean create closes the dialog; a half-failed one must not, because the
     merchant exists and the user has nowhere else to retry the logo from. So
     the form becomes that merchant's EDIT form in place: same component, same
     typed values (they are the created merchant's, by construction), now with
     `MerchantLogoField` mounted and pointed at a real id.

     Held here rather than handed to the caller through a new prop because
     nothing outside needs to know: `Merchants.tsx`'s own `form` state only
     decides whether the dialog is mounted, and it already is. */
  const [created, setCreated] = useState<Merchant | null>(null)
  const editing = editingProp ?? created ?? undefined
  const isGlobal = editing?.is_global === true
  const [name, setName] = useState(editing?.name ?? '')
  const [type, setType] = useState<MerchantType>(editing?.type ?? 'business')
  const [notes, setNotes] = useState(editing?.notes ?? '')
  /* The icon pane, and with it the pick — #461.

     `icon` is no longer a free string. Backend #403/#404 made `POST`/`PATCH
     /merchants` validate it against the same 1,518-slug catalogue the
     categories use, and 422 the WHOLE write for anything else — including all
     88 of the bundled design slugs this form used to offer, so every save with
     an icon chosen failed. The picker now reads `/api/v1/category-icons`, the
     allowlist itself.

     `pane.picked` is the icon chosen IN THIS SESSION, never the stored one.
     That distinction is the wire rule below: a stored value may be a legacy
     slug (an iOS SF Symbol, or one of the 88) that would 422 if resent on a
     save that never touched the icon. `storedIcon` only decides what the grid
     shows as SELECTED, which sends nothing. */
  const pane = useIconPane({ storedIcon: editing?.icon })
  const picked = pane.picked
  const [error, setError] = useState<string | undefined>(undefined)
  const create = useCreateMerchant()
  const update = useUpdateMerchant()
  const upload = useUploadLogo()
  /* The bytes, held until there is an id to upload them against — `POST
     /merchants/{id}/logo` is the only upload endpoint and creation is what
     mints the id (#468).

     The object URL travels WITH the file rather than being derived during
     render: `createObjectURL` allocates, so calling it per render would leak a
     handle a frame, and revoking the previous one is the cleanup that pairs
     with it. `logoError` is separate from `error` (the name field's) so a
     rejected file does not blank a name message and vice versa. */
  const [logo, setLogo] = useState<{ file: File; url: string } | null>(null)
  const [logoError, setLogoError] = useState<string | undefined>(undefined)
  const logoUrl = logo?.url
  useEffect(() => {
    if (logoUrl === undefined) return
    return () => URL.revokeObjectURL(logoUrl)
  }, [logoUrl])
  const toast = useToast()
  // `'form'`, matching `CategoryForm.tsx`'s own type segment — the same
  // density as the `Field` this fieldset sits beside.
  const rowHeight = useHitTarget('form')

  const pending = create.isPending || update.isPending || upload.isPending

  function pickLogo(file: File) {
    setLogoError(undefined)
    setLogo({ file, url: URL.createObjectURL(file) })
  }

  function clearLogo() {
    setLogoError(undefined)
    setLogo(null)
  }
  // NOT `pending || trimmed === ''`. `CategoryForm.tsx` disables its submit on
  // an empty name and states the reason in a footer hint instead — but this
  // form's own test (Task 7, step 1) presses Create on an empty field and
  // expects a message ON THE FIELD, `role="alert"`, which a disabled button
  // could never reach. Blocking submission via `disabled` and reporting it
  // via `error` are two different UIs for the same rule; the brief asks for
  // the second, so only `pending` gates the button here.

  async function createMerchant() {
    const trimmed = name.trim()
    const trimmedNotes = notes.trim()
    const merchant = await create.mutateAsync({
      name: trimmed,
      type,
      // Only when the user picked one, and then exactly the slug the
      // catalogue returned. Otherwise the key is OMITTED, not sent as null:
      // `icon` is optional on every merchant write path, and an absent field
      // is a different claim from an explicit null.
      ...(picked === null ? {} : { icon: picked.slug }),
      ...(trimmedNotes === '' ? {} : { notes: trimmedNotes }),
    })
    if (logo !== null) {
      try {
        await upload.mutateAsync({ id: merchant.id, file: logo.file })
      } catch {
        /* The half-failed save — the case #468 exists for.
 
           The merchant is REAL: `POST /merchants` answered 201 and nothing
           here undoes it. Rolling it back would be a second write that can
           itself fail, and it would throw away a record the user asked for
           over a logo they can add in one more click. So the message says
           exactly what happened, and the form turns into this merchant's edit
           form (see `created`'s own docblock) where `MerchantLogoField` can
           retry against the same id.
 
           **The picked file is DROPPED across that transition**, deliberately
           — not because it has to be. The `File` sits in memory at `logo.file`
           and nothing requires a file input's `value` to carry it forward; the
           real alternative was to KEEP it and the object URL, and offer an
           explicit "Retry upload" button that calls `upload.mutateAsync({ id:
           merchant.id, file: logo.file })` directly, no re-pick required. That
           was set aside, not ruled out: `MerchantLogoField` below is about to
           mount reading `editing.logo_url`, which for a just-created,
           just-failed merchant is `null` — so a held file next to an avatar
           that still says "no logo" is a state that DISAGREES with what is on
           screen, and disagreeing state is exactly what N1's persistent note
           exists to prevent from happening twice on the same failure.
           Dropping keeps the avatar and the data in the same story: re-picking
           costs one click, and what the user sees is true the instant they see
           it. The object URL is released by the effect above when `logo` goes
           null. */
        toast.show({
          kind: 'error',
          message: `${merchant.name} was created, but the logo could not be uploaded. Choose the image again to retry.`,
        })
        setLogo(null)
        setCreated(merchant)
        return
      }
    }
    toast.show({ kind: 'success', message: `${merchant.name} created` })
    onClose()
  }

  /**
   * Only the fields that actually changed, against `editing`'s own values —
   * the brief's own test (Task 8, step 1) presses Save after touching just
   * the name and expects a PATCH carrying `{ name: … }` alone, not the other
   * two fields restated at their unchanged values.
   *
   * `icon` appears only when the user PICKED one this session — #461, and the
   * same rule `CategoryForm` follows. It used to be diffed against
   * `editing.icon`, which is the defect: a merchant carrying a legacy slug
   * that the catalogue does not know would resend nothing on an untouched
   * save (the diff is empty) but would resend the legacy value the moment any
   * other field moved it into the body — and since backend #403 that is a 422
   * on the whole write. A pick is the only thing that can put `icon` on the
   * wire now, and a pick is always a catalogue slug.
   *
   * `color` still never appears: no control offers one, so it can never
   * differ from what `editing` already carries.
   */
  function changedFields(): MerchantUpdate {
    if (editing === undefined) return {}
    const body: MerchantUpdate = {}
    const trimmed = name.trim()
    if (trimmed !== editing.name) body.name = trimmed
    if (type !== editing.type) body.type = type
    const trimmedNotes = notes.trim()
    const nextNotes = trimmedNotes === '' ? null : trimmedNotes
    if (nextNotes !== (editing.notes ?? null)) body.notes = nextNotes
    if (picked !== null) body.icon = picked.slug
    return body
  }

  async function saveMerchant() {
    if (editing === undefined) return
    const updated = await update.mutateAsync({ id: editing.id, body: changedFields() })
    toast.show({ kind: 'success', message: `${updated.name} saved` })
    onClose()
  }

  async function submit() {
    const trimmed = name.trim()
    if (trimmed === '') {
      setError('Name is required.')
      return
    }
    try {
      if (editing === undefined) {
        await createMerchant()
      } else {
        await saveMerchant()
      }
    } catch (cause) {
      // See `messageFor`'s own docblock for what each status means and
      // where it comes from. 400 goes on the field on BOTH create and edit;
      // 403 and 404 only mean anything field-worthy on an edit (`useCreateMerchant`
      // has no id to be forbidden or missing against).
      const status = statusOf(cause)
      const editingNow = editing !== undefined
      if (status === 400 || ((status === 403 || status === 404) && editingNow)) {
        setError(messageFor(status, editingNow))
        return
      }
      // A 422 is FastAPI's own genuine validation failure — issue #473. The
      // real body is `{ detail: ValidationError[] }`, never a bare string,
      // and EVERY entry is surfaced: one routed onto the Name field when its
      // `loc` names it, the rest folded into one toast, labelled where this
      // form has a label for the field (`FIELD_LABELS`) and bare otherwise —
      // never the wire's own field name. Falls through to `messageFor`'s
      // generic copy only when the body carries no usable `detail` at all.
      if (status === 422) {
        const body = cause instanceof MerchantsError ? cause.body : undefined
        const { fieldMessages, otherEntries } = routeValidationErrors(body, KNOWN_FIELDS)
        const nameMessages = fieldMessages.get('name')
        if (nameMessages !== undefined || otherEntries.length > 0) {
          if (nameMessages !== undefined) setError(nameMessages.join('; '))
          if (otherEntries.length > 0) {
            toast.show({ kind: 'error', message: otherEntries.map(describeOtherEntry).join('; ') })
          }
          return
        }
      }
      toast.show({ kind: 'error', message: messageFor(status, editingNow) })
    }
  }

  async function archive() {
    if (editing === undefined) return
    try {
      const updated = await update.mutateAsync({ id: editing.id, body: { is_active: false } })
      toast.show({ kind: 'success', message: `${updated.name} archived` })
      onClose()
    } catch {
      toast.show({ kind: 'error', message: 'The merchant could not be archived.' })
    }
  }

  return (
    <IconPaneDialog
      pane={pane}
      onClose={onClose}
      surface="glass"
      label={editing ? `Edit ${editing.name}` : 'New merchant'}
    >
      <form
        className="merchant-form"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <h3 className="merchant-form__title">{editing ? 'Edit merchant' : 'New merchant'}</h3>

        {/* The half-failed save's PERSISTENT explanation — #468 architecture
            review, N1. The toast that fires alongside `setCreated` below dies
            after `useStackFocus.ts`'s 5000ms, but this dialog does not close:
            it silently becomes `created`'s edit form, `Overlay`'s own `label`
            moving from "New merchant" to `Edit ${name}` with it — and a screen
            reader does not re-announce an already-open dialog's accessible
            name, so once the toast's live region has gone quiet an AT user has
            no signal at all that anything happened. `role="status"` so it is
            announced once on arrival without demanding the interruption
            `role="alert"` would; it stays on screen for as long as the state
            it describes does, which is exactly what a transient toast cannot
            do. The toast itself is kept, not replaced — it is still the first,
            fastest signal for a sighted user watching the dialog at the
            moment of failure; this note is what survives past it. */}
        {created !== null && (
          <p className="merchant-form__note" role="status">
            {created.name} was created. Its logo did not upload — choose the
            image again to retry.
          </p>
        )}

        {isGlobal && (
          <p className="merchant-form__note">
            This merchant is shared reference data and cannot be edited.
          </p>
        )}

        {/* Logo endpoints need an id — Task 7's own docblock — so this only
            ever mounts once a merchant already exists, and never for one that
            is global: `upload_merchant_logo`/`delete_merchant_logo` are not
            documented as exempt from the same user_id ownership check every
            other write on this resource has, so there is no reason to expect
            either would succeed against shared reference data. */}
        {editing !== undefined && !isGlobal && <MerchantLogoField merchant={editing} />}

        {/* The CREATE path's own logo control — #468.

            The same picker and the same rules as the edit field above; what
            differs is only when the bytes leave: there is no id yet, so they
            are held in `logo` and POSTed by `createMerchant` the moment the
            server answers with one. A file that could never be accepted is
            refused HERE, before any merchant exists for it.

            The preview is the confirmation that the pick registered — the
            native input is invisible by design, so without it nothing on
            screen changes when a file is chosen. */}
        {editing === undefined && (
          <div className="merchant-logo">
            {logo !== null && (
              <img className="merchant-logo__preview" src={logo.url} alt="Logo preview" />
            )}
            <div className="merchant-logo__controls">
              <LogoPicker
                pending={pending}
                busyLabel="Creating…"
                onPick={pickLogo}
                onReject={setLogoError}
              />
              {logo !== null && (
                <Button type="button" variant="chip" size="form" disabled={pending} onClick={clearLogo}>
                  Remove image
                </Button>
              )}
            </div>
            {logoError !== undefined && (
              <span className="merchant-logo__error" role="alert">
                {logoError}
              </span>
            )}
          </div>
        )}

        <Field
          label="Name"
          value={name}
          error={error}
          size="form"
          placeholder="e.g. Costco"
          disabled={isGlobal}
          onChange={(e) => {
            setName(e.target.value)
            setError(undefined)
          }}
        />

        <div className="merchant-form__group">
          <span className="merchant-form__group-label" aria-hidden="true">
            {FIELD_LABELS.type}
          </span>
          <div className="merchant-form__seg" role="radiogroup" aria-label="Type">
            {TYPES.map((t) => (
              <label key={t} className="merchant-form__seg-item" style={{ minHeight: rowHeight }}>
                <input
                  className="merchant-form__input"
                  type="radio"
                  name="merchant_type"
                  checked={type === t}
                  disabled={isGlobal}
                  onChange={() => setType(t)}
                />
                {TYPE_LABEL[t]}
              </label>
            ))}
          </div>
        </div>

        {/* The icon — M11-R4, repointed at the backend catalogue by #461.

            A merchant's icon is what stands in when it has no logo — only
            Plaid's globals carry one — so for the merchants a user creates
            themselves this IS the identity control.

            A PANE, not an inline grid, and the same pane the category form
            opens (`platform/icons/IconPane`). The catalogue is 1,518 icons
            across 53 groups: inline it would push Type, Notes and Create
            below the fold and clip the group tabs, which is exactly what #455
            found and fixed for categories. It also means the catalogue is not
            fetched at all for a user who only came to rename a merchant.

            ABSENT for a global merchant rather than disabled — M11-R17, and
            the same call the actions row below makes. A global merchant has
            no write path at all, so a picker here could only ever produce a
            403.

            A VISIBLE heading as well as the button's own name: the label
            names the group for a sighted user, and the group is not a
            labelled form control that would get one for free. */}
        {!isGlobal && (
          <div className="merchant-form__group">
            <span className="merchant-form__group-label" aria-hidden="true">
              {FIELD_LABELS.icon}
            </span>
            <IconPaneOpener pane={pane} />
          </div>
        )}

        <div className="merchant-form__group">
          <label className="merchant-form__group-label" htmlFor="merchant-form-notes">
            {FIELD_LABELS.notes}
          </label>
          <textarea
            id="merchant-form-notes"
            className="merchant-form__notes"
            value={notes}
            disabled={isGlobal}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <div className="merchant-form__actions">
          <Button type="button" variant="chip" size="form" onClick={onClose}>
            Cancel
          </Button>
          {/* Archive is its own PATCH, independent of Save — pressing it sends
              `{ is_active: false }` regardless of any unsaved edit sitting in
              the fields above, the same way `CategoryTable.tsx`'s own row
              actions are independent of its dialog. Offered only for a
              user's own ACTIVE merchant: a global row has no write path at
              all (see the docblock), and an already-archived one has nothing
              left for this control to do — Task 10 is where a merchant
              actually leaves the list, not here. */}
          {editing !== undefined && !isGlobal && editing.is_active && (
            <Button type="button" variant="outline" size="form" disabled={pending} onClick={() => void archive()}>
              Archive
            </Button>
          )}
          {/* A global merchant offers no write action at all — see the
              docblock's `editing.is_global` branch. No Save, no "copy this",
              nothing but Cancel above. */}
          {!isGlobal && (
            <Button type="submit" variant="primary" size="form" disabled={pending}>
              {editing ? (pending ? 'Saving…' : 'Save') : pending ? 'Creating…' : 'Create merchant'}
            </Button>
          )}
        </div>
      </form>
    </IconPaneDialog>
  )
}
